import { useState, useEffect, useMemo } from 'react';
import { useLocale } from '@adsk/alloy-react-locale';
import { useCurrentProjectId, PRODUCT_IDS } from '@adsk/alloy-provider';
import useSheetsPermissions from './useSheetsPermissions';
import useSubmittalsPermissions from './useSubmittalsPermissions';
import useRFIsPermissions from './useRFIsPermissions';
import useIssuesPermissions from './useIssuesPermissions';
import { getToolsUrls } from './utils';

const useProductsTools = ({
  hasAccessToBuild,
  shouldUseNewIssuePermissions,
}) => {
  const [productsTools, setProductsTools] = useState({});
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const projectId = useCurrentProjectId();

  const toolsUrls = useMemo(() => getToolsUrls(projectId), [projectId]);
  const sheetsPermissions = useSheetsPermissions(hasAccessToBuild);
  const submittalsPermissions = useSubmittalsPermissions(hasAccessToBuild);
  const rfisPermissions = useRFIsPermissions();
  const issuesPermissions = useIssuesPermissions({
    shouldUseNewIssuePermissions,
  });

  useEffect(() => {
    const tools = {
      [PRODUCT_IDS.BUILD]: [
        {
          name: T.translate('PERMISSIONS.TOOLS.FILES'),
          url: toolsUrls.buildFiles,
        },
        {
          name: T.translate('PERMISSIONS.TOOLS.ISSUES'),
          url: toolsUrls.issues,
          permissions: issuesPermissions,
        },
        {
          name: T.translate('PERMISSIONS.TOOLS.SHEETS'),
          url: toolsUrls.sheets,
          permissions: sheetsPermissions,
        },
        {
          name: T.translate('PERMISSIONS.TOOLS.SUBMITTALS'),
          url: toolsUrls.submittals,
          permissions: submittalsPermissions,
        },
        {
          name: T.translate('PERMISSIONS.TOOLS.RFIs'),
          url: toolsUrls.rfis,
          permissions: rfisPermissions,
        },
      ],
      [PRODUCT_IDS.DOCS]: [
        {
          name: T.translate('PERMISSIONS.TOOLS.FILES'),
          url: toolsUrls.docsFiles,
        },
      ],
      [PRODUCT_IDS.COST]: [
        {
          name: T.translate('PERMISSIONS.VIEW_ALL_TOOLS'),
          url: toolsUrls.cost,
        },
      ],
    };

    setProductsTools(tools);
  }, [
    issuesPermissions,
    sheetsPermissions,
    submittalsPermissions,
    rfisPermissions,
    toolsUrls,
    T,
  ]);

  return productsTools;
};

export default useProductsTools;
